<template>
    <div class="step-container" :class="{ 'mobile': getMobileAppState, 'active': stepNumber === activeStep }">
        <div class="step" :class="{ 'complete': stepIsCompleted }">
            <icon-check-line v-if="stepIsCompleted && !getMobileAppState"/>
            <span v-else>{{ stepNumber }}</span>
            <svg v-if="getMobileAppState" viewBox="-50 -50 100 100" stroke-width="8">
				<circle :r="radius"></circle>
				<circle :r="radius" :stroke-dasharray="circumference" :stroke-dashoffset="usedCircumference"></circle>
			</svg>
        </div>
        <div class="label-container">
            <div class="steps-counter">{{ `Passo ${stepNumber}/${totalSteps}` }}</div>
            <div class="description">{{ description }}</div>
        </div>
    </div>
</template>

<script setup>
import IconCheckLine from '@/assets/icons/check-line.svg?inline'

/* Vue */
import { computed } from 'vue'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* mapGetters */
const { getMobileAppState } = mapGetters()

/** Props received
@stepNumber { Number } - number of the step
@activeStep { Number } - number of the active step
@totalSteps { Number } - number of total steps
@description { String } - step description
*/
const props = defineProps({
    stepNumber: Number,
    activeStep: Number,
    totalSteps: Number,
    description: String
})

const radius = 45
const circumference = 2 * Math.PI * radius
const usedCircumference = computed(() => {
    return circumference - (props.stepNumber / props.totalSteps) * circumference
})

const stepIsCompleted = computed(() => {
    return props.activeStep > props.stepNumber
})
</script>

<style lang="scss" scoped>
.step-container {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 8px;

    .step {
        width: 48px;
        height: 48px;
        border-radius: 50%;

        display: flex;
        align-items: center;
        justify-content: center;

        border: 2px solid var(--secondary);
        background-color: var(--secondary-light-4);

        @include H5-Medium;
        line-height: 20px;
        color: var(--secondary);

        svg {
            width: 24px;
            height: 24px;

            :deep(.change-color-fill) {
                fill: var(--secondary);
            }
        }
    }

    .label-container {
        .steps-counter {
            @include H8-Regular;
            line-height: 13px;
            color: var(--secondary);
        }

        .description {
            @include H5-Medium;
            line-height: 20px;
            color: var(--secondary);
        }
    }

    &.active {
        .step {
            border-color: var(--secondary-dark-3);
            background-color: var(--secondary-dark-3);

            color: var(--secondary-light-4);
        }

        .label-container {
            .steps-counter {
                color: var(--secondary-dark-3);
            }

            .description {
                color: var(--secondary-dark-3);
            }
        }
    }

    &.mobile {
        flex-direction: row;
        align-items: center;
        gap: 16px;

        position: relative;

        flex-shrink: 0;

        .step {
            border: 4px solid transparent;
            background-color: transparent;

            color: var(--secondary-dark-3);

            span {
                position: absolute;
            }

            svg {
                width: 40px;
                height: 40px;
            }

            [r] {
                fill: none;
                stroke: var(--secondary-light-1);
            }

            [r] + [r] {
                transform: rotate(270deg);
                stroke: var(--secondary-dark-3);

                transition: all 1s ease;
            }
        }

    }
}
</style>
